/* eslint-disable max-depth */
import useGeneralDelivery from '../../../../../../../hooks/useGeneralDelivery';
import { usePushdownContext } from '../../../../../../../providers/Pushdown/PushdownProvider';
import {
    THomeAddress,
    useHomeAddress,
} from '../../../../../providers/HomeAddressProvider';
import { IErrorState } from '../../../../../providers/reducers/ErrorReducer';
import {
    usePersonalInformationError,
} from '../../../../../providers/PersonalInformationErrorProvider';
import {
    TPersonalInformation,
    usePersonalInformation,
} from '../../../../../providers/PersonalInformationProvider';
import { useHomeAddressError } from '../../../../../providers/HomeAddressErrorProvider';
import { usePageProperties } from '../../../../../providers/PagePropertiesProvider';
import { calculateAge, isValidEmailFormat, regexValidation } from '../../../../../../../utils/cob-utils';
import { getFormattedDate, isDateValid } from '../../../../../../../utils/acquisition-utils';
import { useAddressValidationState } from '../../../../../../../components/PqDataEntryForm/providers/AddressValidationProvider';
import getPushdownError, { IFieldName, TErrorType } from '../../../../../../../providers/Pushdown/util/getPushdownError';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { useState } from 'react';
import usePushupMessages from '../../../../../../../components/PqDataEntryForm/hooks/usePushupMessages';
import useBriteVerify from '../../../../../../../hooks/useBriteVerify';
import SessionStorage from '../../../../../../../utils/SessionStorage';
import { generateErrorMessage } from '../../../../../../../utils/error-message';

interface IValidationError {
    message: 'validation error';
    payload: {
        personalInformationErrorsFound: Record<string, IErrorState>;
        homeAddressErrorsFound: Record<string, IErrorState>;
        pushDownMessages: string[];
    };
}

/**
 * validates inputs before redirection
 */
export default function useValidateForm() {
    const { errorDesc, errorTitle } = usePageProperties();
    const { personalInformation } = usePersonalInformation();
    const { personalInformationError, dispatch: piDispatch } =
        usePersonalInformationError();
    const { homeAddress, showHomeAddress, setShowHomeAddress } = useHomeAddress();
    const { homeAddressError, dispatch: haDispatch } = useHomeAddressError();
    const { emailValidationApi } = useBriteVerify();

    const { matchGeneralDelivery } = useGeneralDelivery();
    const matchesGeneralDelivery = matchGeneralDelivery(
        personalInformation.MailingAddressLine1,
    );
    const { closePushdown, showPushdown } = usePushdownContext();
    const { validationRef } = useAddressValidationState();
    const [ elist ] = useState<String[]>(PathUtils.getMetaPropertyValue('cq:elist')?.split(',').map(email => atob(email)) ?? []);
    const { clearHashFromUrl } = usePushupMessages();

    const validateForm = async () => {
        closePushdown();
        const personalInformationErrorsFound: Record<string, IErrorState> = {};
        const homeAddressErrorsFound: Record<string, IErrorState> = {};
        const pushDownMessagesObj = new Map<string, string>([
            ['FirstName', ''],
            ['LastName', ''],
            ['MailingAddressLine1', ''],
            ['MailingAddressLine2', ''],
            ['MailingCity', ''],
            ['MailingState', ''],
            ['MailingZipCode', ''],
            ['HomeAddressLine1', ''],
            ['HomeAddressLine2', ''],
            ['HomeCity', ''],
            ['HomeState', ''],
            ['HomeZipCode', ''],
            ['EmailAddress', ''],
            ['Ssn', ''],
            ['dob', ''],
        ]);
        const isAuthor = ['4502', '3000'].some((entry) => window.location.port.includes(entry));
        const isAdobeMode = SessionStorage.contains('adobeMode');
        const useEmailApi = SessionStorage.contains('useEmailApi');

        let closeHomeAddressFields = false;
        const regex = regexValidation();

        for (const key in personalInformationError) {
            const typedKey = key as keyof TPersonalInformation;

            const { required, message, isValidValue, isInError } =
                personalInformationError[typedKey];

            if (
                typedKey === 'FirstName'
                && personalInformation?.FirstName 
                && (
                    regex.disallow.last_name.test(personalInformation.FirstName)
                    || !regex.allow.last_name.test(personalInformation.FirstName)
                )
            ) {
                const firstNamePushdownError = getPushdownError('FirstName', personalInformation.FirstName === '' ? 'RequiredField' : 'NotValid');
                pushDownMessagesObj.set('FirstName', firstNamePushdownError.htmlError || firstNamePushdownError.ErrorMessage);
                personalInformationErrorsFound.FirstName = {
                    isValidValue: false,
                    required,
                    message,
                    isInError: true,
                };
            }

            if (
                typedKey === 'LastName'
                && personalInformation?.LastName 
                && (
                    regex.disallow.last_name.test(personalInformation.LastName)
                    || !regex.allow.last_name.test(personalInformation.LastName)
                )
            ) {
                const lastNamePushdownError = getPushdownError('LastName', personalInformation.LastName === '' ? 'RequiredField' : 'NotValid');
                pushDownMessagesObj.set('LastName', lastNamePushdownError.htmlError || lastNamePushdownError.ErrorMessage);
                personalInformationErrorsFound.LastName = {
                    isValidValue: false,
                    required,
                    message,
                    isInError: true,
                };
            }

            if (typedKey === 'dob' && personalInformation.dob) {

                const dobSplit = personalInformation.dob.split('/');

                const DOBDay = dobSplit[1];
                const DOBMonth = dobSplit[0];
                const DOBYear = dobSplit[2];

                const formattedDob = getFormattedDate(
                    {
                        month: { value: DOBMonth },
                        day: { value: DOBDay },
                        year: { value: DOBYear },
                    },
                );
                personalInformation.dob = formattedDob;

                const age = calculateAge(personalInformation.dob);

                let dobError = false;

                if (!isDateValid(personalInformation.dob, homeAddress.HomeState || personalInformation.MailingState)) {
                    pushDownMessagesObj.set(typedKey, generateErrorMessage('<a href="#dob-field-container">Date of Birth</a>'));
                    dobError = true;
                } else if (age < 18) {
                    pushDownMessagesObj.set(typedKey, 'Must be at least 18 years of age to apply. You are not eligible for this program');
                    dobError = true;
                }

                if (isInError || !isValidValue || dobError) {
                    personalInformationErrorsFound.dob = {
                        isValidValue: false,
                        required,
                        message,
                        isInError: true,
                    };

                }
            }

            // checks all Personal Information Fields (Defaults, Email, and Phone Number)
            if (required || !isValidValue) {
                
                // if line1 is not empty check if it matches line2
                if (typedKey === 'MailingAddressLine1' && personalInformation[typedKey]) {
                    if (
                        personalInformation.MailingAddressLine1 ===
                        personalInformation.MailingAddressLine2
                    ) {
                        pushDownMessagesObj.set('MailingAddressLine2', '<a href="#mailingAddress-field-container">Mailing Address</a> cannot be the same as Apt/Unit#');
                    }
                }

                if (
                    typedKey === 'MailingZipCode'
                    && personalInformation?.MailingZipCode?.length < 5
                    && personalInformation?.MailingZipCode?.length > 0
                ) {
                    pushDownMessagesObj.set('MailingZipCode', getPushdownError('MailingZipCode', 'NotValid').htmlError);
                    personalInformationErrorsFound.MailingZipCode = {
                        isValidValue: false,
                        required,
                        message,
                        isInError: true,
                    };
                }

                if (!personalInformation[typedKey]) {
                    //if any mailing address field is empty, error that field
                    personalInformationErrorsFound[typedKey] = {
                        isValidValue: false,
                        required,
                        message,
                        isInError: true,
                    };
                    const fieldName: IFieldName = typedKey === 'dob' ? 'DateOfBirth' : typedKey as IFieldName;
                    const errorObj = getPushdownError(fieldName, personalInformation[typedKey] === '' ? 'RequiredField' : 'NotValid');
                    const errorMessage = errorObj.htmlError
                        ? errorObj.htmlError
                        : errorObj.ErrorMessage;

                    pushDownMessagesObj.set(typedKey, errorMessage);
                }
            }
        }

        // This will trigger SSN pushdown message if length is <9
        if (personalInformation?.Ssn && personalInformation?.Ssn.length < 9) {
            pushDownMessagesObj.set('Ssn', generateErrorMessage('<a href="#socialSecurity-field-container">Social Security Number</a>'));
            pushDownMessagesObj.set('Ssn', getPushdownError('Ssn', personalInformation.Ssn === '' ? 'RequiredField' : 'NotValid').htmlError);
        }
        const emailAddress = personalInformation.EmailAddress;
        let emailErrorType: TErrorType | null = null;
        if (!emailAddress) {
            emailErrorType = 'RequiredField';
        } else if (!isValidEmailFormat(emailAddress)) {
            emailErrorType = 'NotValid';
        } else if (elist.includes(emailAddress.toLowerCase().substring(emailAddress.indexOf('@') + 1))) {
            emailErrorType = 'ThirdParty';
        } else if (
            ((!isAuthor && !isAdobeMode) || (isAdobeMode && useEmailApi))
            && !(await emailValidationApi(emailAddress))
        ) {
            emailErrorType = 'ThirdParty';
        } else if (
            !personalInformationError.EmailAddress.isValidValue
            || personalInformationError.EmailAddress.isInError
        ) {
            emailErrorType = 'ThirdParty';
        }

        if (emailErrorType) {
            const emailAddressMessage = getPushdownError('EmailAddress', emailErrorType);
            pushDownMessagesObj.set('EmailAddress', emailAddressMessage.htmlError);
            personalInformationErrorsFound.EmailAddress = {
                isValidValue: false,
                required: true,
                isInError: true,
                errorType: emailErrorType,
            };
        }

        // checks all Home Address Fields
        if (!closeHomeAddressFields && (matchesGeneralDelivery || showHomeAddress)) {
            for (const key in homeAddressError) {
                const typedKey = key as keyof THomeAddress;
                const { required, message } = homeAddressError[typedKey];

                if (required) {
                    //if any home address field is empty, error that field
                    if (!homeAddress[typedKey]) {
                        homeAddressErrorsFound[typedKey] = {
                            isValidValue: false,
                            required,
                            message,
                            isInError: true,
                        };
                        const errorObj = getPushdownError(key as any, 'RequiredField');
                        const errorMessage = errorObj.htmlError 
                            ? errorObj.htmlError 
                            : errorObj.ErrorMessage;
                            
                        pushDownMessagesObj.set(key, errorMessage);
                    }

                    // if line1 is not empty check if it matches line2
                    if (key === 'HomeAddressLine1' && homeAddress[typedKey]) {
                        if (
                            homeAddress.HomeAddressLine1 ===
                            homeAddress.HomeAddressLine2
                        ) {
                            pushDownMessagesObj.set('HomeAddressLine2', '<a href="#homeAddress-field-container">Home Address</a> cannot be the same as Apt/Unit#');
                        }
                    }

                    if (
                        typedKey === 'HomeZipCode'
                        && homeAddress?.HomeZipCode?.length < 5
                        && homeAddress?.HomeZipCode?.length > 0
                    ) {
                        pushDownMessagesObj.set('HomeZipCode' ,getPushdownError('HomeZipCode', 'NotValid').htmlError);
                        homeAddressErrorsFound.HomeZipCode = {
                            isValidValue: false,
                            required,
                            message,
                            isInError: true,
                        }
                    }
                }
            }
        }

        if (closeHomeAddressFields) {
            setShowHomeAddress(false);
        }

        const piErrorsFound =
            Object.keys(personalInformationErrorsFound).length > 0;
        const haErrorsFound = Object.keys(homeAddressErrorsFound).length > 0;
        if (piErrorsFound || haErrorsFound) {
            return Promise.reject({
                message: 'validation error',
                payload: {
                    personalInformationErrorsFound,
                    homeAddressErrorsFound,
                    pushDownMessages: [...pushDownMessagesObj.values()].filter(e => e !== ''),
                },
            });
        }

        if (!validationRef.current.isValid) {
            return Promise.reject({ message: 'loqate error' });
        }

        return Promise.resolve();
    };
    const handleValidationError = ({
        personalInformationErrorsFound,
        homeAddressErrorsFound,
        pushDownMessages,
    }: IValidationError['payload']) => {
        clearHashFromUrl(false);
        const piErrorsFound =
            Object.keys(personalInformationErrorsFound).length > 0;
        const haErrorsFound = Object.keys(homeAddressErrorsFound).length > 0;

        if (piErrorsFound) {
            piDispatch({
                type: 'batchSetValid',
                payload: personalInformationErrorsFound,
            });
        }

        if (matchesGeneralDelivery || showHomeAddress) {
            if (haErrorsFound) {
                haDispatch({
                    type: 'batchSetValid',
                    payload: homeAddressErrorsFound,
                });
            }
        }

        if (piErrorsFound || haErrorsFound) {
            showPushdown({
                header: errorTitle,
                description: errorDesc,
                message: pushDownMessages,
                color: 'error',
            });
        }
    };

    return {
        validateForm,
        handleValidationError,
    };
}
